/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import _ from 'lodash';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import type { Moment } from 'moment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  NativeSelect,
  Typography,
  Button,
  Link,
  Dialog,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress
} from '@material-ui/core';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import CloseIcon from '@material-ui/icons/Close';
import CancelIcon from '@material-ui/icons/Cancel';
import muiTheme from '@this/components/appoint/mui_theme';
import type { OpenMeetingType } from '@this/domain/appoint/open_meeting';
import type OpenMeeting from '@this/domain/appoint/open_meeting';
import {
  openMeetingStartDateOptions,
  periodOptions,
  defaultOpenMeetingSlots
} from '@this/domain/appoint/open_meeting';
import type OpenMeetingBlockedSlot from '@this/domain/appoint/open_meeting_blocked_slot';
import type Calendar from '@this/domain/appoint/calendar';
import type MeetingTitleHistory from '@this/domain/appoint/meeting_title_history';
import type UserSetting from '@this/domain/appoint/user_setting';
import Utils from '@this/domain/appoint/utils';
import AppointContext from '@this/components/appoint/appoint_context';
import PlaceInput from '@this/components/appoint/adjustments/new/place_input';
import ParticipantInput from '@this/components/appoint/adjustments/new/participant_input';
import type { Plan } from '@this/components/appoint/common';
import { H2 } from '@this/components/appoint/common';
import localStorage from '@this/lib/local_storage';
import BlockedSlotForm from './blocked_slot_form';

const OPEN_MEETING_KEY = 'openMeetingDataV1';

const durations = [
  { label: '15分', value: 15 },
  { label: '30分', value: 30 },
  { label: '45分', value: 45 },
  { label: '60分', value: 60 },
  { label: '90分', value: 90 },
  { label: '120分', value: 120 },
  { label: '180分', value: 180 }
];

const buffers = [
  { label: '0分', value: 0 },
  { label: '15分', value: 15 },
  { label: '30分', value: 30 },
  { label: '45分', value: 45 },
  { label: '60分', value: 60 },
  { label: '90分', value: 90 },
  { label: '120分', value: 120 },
  { label: '180分', value: 180 }
];

const eventsPerDayOptions = [...Array(50)].map((_, i) => i + 1);

type Props = {
  formTitle: string;
  isLoading: boolean;
  openMeeting: OpenMeeting;
  calendars: Calendar[];
  currentPlan: Plan;
  onChangeParticipant: () => void;
  openMeetingBlockedSlotRemove: (s: OpenMeetingBlockedSlot) => void;
  openMeetingBlockedSlotsChange: (blockedSlots: OpenMeetingBlockedSlot[]) => void;
  isFetchingEvents: boolean;
  showBlockedSlotsForm: boolean;
  setShowBlockedSlotsForm: (visible: boolean) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitErrorMessage: string | null;
  trialUsed: boolean;
  meetingTitleHistories: MeetingTitleHistory[];
  userSetting: UserSetting;
};

const Form = observer(
  ({
    formTitle,
    isLoading,
    openMeeting,
    calendars,
    currentPlan,
    onChangeParticipant,
    openMeetingBlockedSlotRemove,
    openMeetingBlockedSlotsChange,
    isFetchingEvents,
    showBlockedSlotsForm,
    setShowBlockedSlotsForm,
    onSubmit,
    isSubmitting,
    submitErrorMessage,
    trialUsed,
    meetingTitleHistories,
    userSetting // 候補日自動計算用
  }: Props) => {
    const history = useHistory();

    const contextValue = useContext(AppointContext);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showZoomAuthDialog, setShowZoomAuthDialog] = useState<boolean>(false);

    const [startTime, setStartTime] = useState<string>(openMeeting?.timeText('start') || '09:00');
    const [endTime, setEndTime] = useState<string>(openMeeting?.timeText('end') || '18:00');

    const eventTimes: string[] = Array.from(Array(24).keys())
      .map(h => [0, 15, 30, 45].map(m => `${`0${h}`.slice(-2)}:${`0${m}`.slice(-2)}`))
      .flat();

    useEffect(() => {
      localStorage.setItem(OPEN_MEETING_KEY, JSON.stringify(openMeeting.submitParams()));
    }, [
      openMeeting.title,
      openMeeting.workingTitle,
      openMeeting.place,
      openMeeting.placeAddress,
      openMeeting.description,
      openMeeting.startDate,
      openMeeting.diffToStartDate,
      openMeeting.period,
      openMeeting.endDate,
      openMeeting.duration,
      openMeeting.bufferBetween,
      openMeeting.eventsPerDay,
      openMeeting.meetingType,
      openMeeting.participants,
      openMeeting.openMeetingSlots,
      startTime,
      endTime
    ]);

    const setTimes = () => {
      setStartTime(openMeeting.timeText('start'));
      setEndTime(openMeeting.timeText('end'));
    };

    useEffect(() => {
      if (openMeeting) {
        setTimes();
      }
    }, [openMeeting.openMeetingSlots]);

    const handleAddParticipant = (participant: Calendar) => {
      openMeeting.participants.push(participant);
      onChangeParticipant();
    };

    const handleRemoveParticipant = (participant: Calendar) => {
      openMeeting.removeParticipant(participant);
      onChangeParticipant();
    };

    const handleChangeDuration = (e: React.ChangeEvent<{ value: unknown }>) => {
      if (typeof e.target.value === 'string') {
        openMeeting.duration = parseInt(e.target.value, 10);
      } else if (typeof e.target.value === 'number') {
        openMeeting.duration = e.target.value;
      }
    };

    const handleChangeBufferBetween = (e: React.ChangeEvent<{ value: unknown }>) => {
      if (typeof e.target.value === 'string') {
        openMeeting.bufferBetween = parseInt(e.target.value, 10);
      } else if (typeof e.target.value === 'number') {
        openMeeting.bufferBetween = e.target.value;
      }
    };

    const handleChangeSelectedStartDate = (e: React.ChangeEvent<{ value: unknown }>) => {
      if (typeof e.target.value === 'string') {
        openMeeting.diffToStartDate = e.target.value;
      }
    };

    const handleChangePeriod = (e: React.ChangeEvent<{ value: unknown }>) => {
      if (typeof e.target.value === 'string') {
        openMeeting.period = e.target.value;
        openMeeting.endDate = undefined;
      }
    };

    const handleChangeEndDate = (date: Moment | null) => {
      if (date) {
        openMeeting.endDate = date;
      }
    };

    const handleChangeEventsPerDay = (e: React.ChangeEvent<{ value: unknown }>) => {
      if (typeof e.target.value === 'string') {
        openMeeting.eventsPerDay = parseInt(e.target.value, 10);
      } else if (typeof e.target.value === 'number') {
        openMeeting.eventsPerDay = e.target.value;
      }
    };

    const handleUpdateBlockedSlots = () => {
      setShowBlockedSlotsForm(false);
    };

    const handleCancelBlockedSlots = (blockedSlots: OpenMeetingBlockedSlot[]) => {
      openMeetingBlockedSlotsChange(blockedSlots);
      setShowBlockedSlotsForm(false);
    };

    const handleValidation = () => {
      if (openMeeting.title.length > 0) {
        setErrorMessage(null);
        onSubmit();
      } else {
        setErrorMessage('打ち合わせのタイトルを入力してください。');
      }
    };

    const isParticipantEmpty = () => {
      return openMeeting.participants.length === 0;
    };

    const isMultiParticipantPrevented = () => {
      // 当面無料で全機能使えるようにする
      return false;

      // eslint-disable-next-line no-unreachable
      return currentPlan === 'free' && openMeeting.participants.length > 1;
    };

    const isTitleEmpty = () => {
      return openMeeting.title.length === 0;
    };

    const isSubmitButtonAvailable = () => {
      return (
        !isLoading && !isSubmitting && !isParticipantEmpty() && !isMultiParticipantPrevented() && !isTitleEmpty()
      );
    };

    const user = contextValue.user;

    const handleChangeMeetingType = (meetingType: OpenMeetingType) => {
      if (meetingType !== 'zoom' || user?.appointZoomAccessToken) {
        openMeeting.meetingType = meetingType;
        userSetting.meetingType = meetingType;
        axios.put('/aicalendar/user_settings', userSetting.submitParams());
      } else {
        setShowZoomAuthDialog(true);
      }
    };

    const handleTimeChange = (type: 'start' | 'end', time: string) => {
      let tmpStartTime = startTime;
      let tmpEndTime = endTime;
      // eventTimesにおけるindexで前後がわかるため、逆転している場合は4ステップ(1時間)の差をつけて他方も上書く
      let startIndex = eventTimes.indexOf(tmpStartTime);
      let endIndex = eventTimes.indexOf(tmpEndTime);
      if (type === 'start') {
        tmpStartTime = time;
        startIndex = eventTimes.indexOf(time);
        if (startIndex >= endIndex) {
          tmpEndTime = eventTimes[startIndex + 4];
        }
      } else {
        tmpEndTime = time;
        endIndex = eventTimes.indexOf(time);
        if (startIndex >= endIndex) {
          tmpStartTime = eventTimes[endIndex - 4];
        }
      }
      setStartTime(tmpStartTime);
      setEndTime(tmpEndTime);
      if (openMeeting.openMeetingSlots.length === 0) {
        openMeeting.openMeetingSlots = defaultOpenMeetingSlots(openMeeting.id);
      }
      const [startHour, startMinute] = tmpStartTime.split(':').map(str => parseInt(str, 10));
      const [endHour, endMinute] = tmpEndTime.split(':').map(str => parseInt(str, 10));
      openMeeting.openMeetingSlots.forEach(slot => {
        slot.startTime.set('hour', startHour).set('minute', startMinute);
        slot.endTime.set('hour', endHour).set('minute', endMinute);
      });
    };

    const renderTimeSelector = (type: 'start' | 'end') => (
      <AutocompleteBox>
        <Autocomplete
          options={type === 'start' ? eventTimes.slice(0, 92) : eventTimes.slice(4)}
          getOptionLabel={option => option}
          filterOptions={(options, _state) => options}
          inputValue={type === 'start' ? startTime : endTime}
          freeSolo
          onInputChange={(_, value) => {
            if (typeof value === 'string' && value !== '') {
              handleTimeChange(type, value);
            }
          }}
          onChange={(_, selected) => {
            if (selected && typeof selected === 'string' && selected.length > 0) {
              handleTimeChange(type, selected);
            }
          }}
          onClose={(e, reason) => {
            if (reason === 'blur') {
              e.stopPropagation();
            }
          }}
          renderInput={params => (
            <TheTextField
              {...params}
              label={type === 'start' ? '開始時刻' : '終了時刻'}
              InputLabelProps={{ shrink: true }}
            />
          )}
          ListboxProps={{ style: { fontSize: 12, marginLeft: -10 } }}
        />
      </AutocompleteBox>
    );

    const renderZoomAuthDialog = () => (
      <ZoomAuthDialog open={showZoomAuthDialog} onClose={() => setShowZoomAuthDialog(false)}>
        <Paper style={{ position: 'relative' }}>
          <IconButton
            size="small"
            onClick={() => setShowZoomAuthDialog(false)}
            style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <ZoomAuthContent>
            <img
              src="/images/aicalendar/zoom_auth.png"
              height="40"
              alt="Zoomと連携する"
              style={{ display: 'block' }}
            />
            <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.2em' }}>
              Zoomにログインして
              <br />
              連携をしますか？
            </p>
          </ZoomAuthContent>
          <ZoomAuthButtonWrap>
            <Button variant="contained" onClick={() => setShowZoomAuthDialog(false)}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                window.open(history.createHref({ pathname: '/zoom/authorization/new' }));
              }}
            >
              連携
            </Button>
          </ZoomAuthButtonWrap>
        </Paper>
      </ZoomAuthDialog>
    );

    const renderBlockedSlotsForm = () => (
      <BlockedSlotForm
        openMeeting={openMeeting}
        handleBlockedSlotRemove={s => openMeetingBlockedSlotRemove(s)}
        handleSave={() => handleUpdateBlockedSlots()}
        handleCancel={blockedSlots => handleCancelBlockedSlots(blockedSlots)}
      />
    );

    return (
      <>
        {!contextValue.isMobile && showBlockedSlotsForm ? (
          <>{renderBlockedSlotsForm()}</>
        ) : (
          <>
            <div>
              <TheLeftHead display="flex" alignItems="flex-start">
                <H2 style={{ margin: '0 auto 0 0', fontSize: '16px', fontWeight: 'bold' }}>{formTitle}</H2>
                <IconButton size="small" component={RouterLink} to="/">
                  <CloseButton fontSize="small" />
                </IconButton>
              </TheLeftHead>
              {isLoading ? (
                <Box marginTop="20px" textAlign="center">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <LeftSection>
                    <LeftSectionTitle>基本情報</LeftSectionTitle>
                    <LeftSectionIn>
                      <AutocompleteBox withAlert={`${isTitleEmpty()}`}>
                        <Autocomplete
                          options={meetingTitleHistories}
                          getOptionLabel={history => history.title || ''}
                          inputValue={openMeeting.workingTitle}
                          filterOptions={(options, state) => {
                            const q = state.inputValue;
                            return _.filter(options, o => o.title?.indexOf(q) > -1);
                          }}
                          onInputChange={(e, value) => {
                            // enter押下時(eventType === 'onKeyDown')にタイトルを空にしない。
                            const eventType = Utils.dig(e, 'dispatchConfig', 'phasedRegistrationNames', 'bubbled');
                            if (typeof value === 'string' && eventType !== null && eventType !== 'onKeyDown') {
                              openMeeting.workingTitle = value;
                            }
                          }}
                          onChange={(_, selected) => {
                            if (selected && typeof selected !== 'string') {
                              openMeeting.workingTitle = selected.title;
                            }
                          }}
                          openOnFocus
                          freeSolo
                          renderInput={params => (
                            <TheTextField
                              {...params}
                              label="公開URLのタイトル"
                              placeholder="タイトルを入力"
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                          style={{ width: '100%', marginTop: '10px' }}
                        />
                      </AutocompleteBox>
                      {isTitleEmpty() && <Error>タイトルを入力してください</Error>}
                      <LeftSectionSmallTitle>相手に表示される項目</LeftSectionSmallTitle>
                      <LeftSectionInSection>
                        <AutocompleteBox withAlert={`${isTitleEmpty()}`}>
                          <Autocomplete
                            options={meetingTitleHistories}
                            getOptionLabel={history => history.title || ''}
                            inputValue={openMeeting.title}
                            filterOptions={(options, state) => {
                              const q = state.inputValue;
                              return _.filter(options, o => o.title?.indexOf(q) > -1);
                            }}
                            onInputChange={(e, value) => {
                              // enter押下時(eventType === 'onKeyDown')にタイトルを空にしない。
                              const eventType = Utils.dig(
                                e,
                                'dispatchConfig',
                                'phasedRegistrationNames',
                                'bubbled'
                              );
                              if (typeof value === 'string' && eventType !== null && eventType !== 'onKeyDown') {
                                openMeeting.title = value;
                              }
                            }}
                            onChange={(_, selected) => {
                              if (selected && typeof selected !== 'string') {
                                openMeeting.title = selected.title;
                              }
                            }}
                            openOnFocus
                            freeSolo
                            renderInput={params => (
                              <TheTextField
                                {...params}
                                label="タイトル"
                                placeholder="タイトルを入力"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            style={{ width: '100%', marginTop: '10px' }}
                          />
                        </AutocompleteBox>
                        <Box flexGrow={999}>
                          <TextField
                            label="メモ"
                            placeholder="メモを追加"
                            value={openMeeting.description || ''}
                            multiline
                            onChange={e => {
                              openMeeting.description = e.target.value;
                            }}
                            InputLabelProps={{ shrink: true }}
                            style={{ width: '100%', marginTop: '20px' }}
                          />
                        </Box>
                      </LeftSectionInSection>
                      <ParticipantInput
                        isLoading={isFetchingEvents}
                        participants={openMeeting.participants}
                        calendars={calendars}
                        onAdd={handleAddParticipant}
                        onRemove={handleRemoveParticipant}
                      />
                      {isParticipantEmpty() && <Error>参加者を選択してください</Error>}
                    </LeftSectionIn>
                  </LeftSection>
                  <LeftSection>
                    <LeftSectionTitle>候補枠</LeftSectionTitle>
                    <LeftSectionIn>
                      <FormControl style={{ width: '100%', marginTop: '10px' }}>
                        <InputLabel id="duration">打ち合わせ時間</InputLabel>
                        <Select
                          labelId="duration"
                          value={openMeeting.duration}
                          onChange={e => handleChangeDuration(e)}
                        >
                          {durations.map(d => (
                            <MenuItem value={d.value} key={d.value}>
                              {d.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl style={{ width: '100%', marginTop: '20px' }}>
                        <InputLabel id="bufferBetween">前後の確保時間</InputLabel>
                        <Select
                          labelId="bufferBetween"
                          value={openMeeting.bufferBetween}
                          onChange={e => handleChangeBufferBetween(e)}
                        >
                          {buffers.map(d => (
                            <MenuItem value={d.value} key={d.value}>
                              {d.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <LeftSectionSmallTitle>抽出期間を設定</LeftSectionSmallTitle>
                      <LeftSectionInSection>
                        <FormControl style={{ width: '100%', marginTop: '10px' }}>
                          {renderTimeSelector('start')}
                        </FormControl>
                        <FormControl style={{ width: '100%', marginTop: '10px' }}>
                          {renderTimeSelector('end')}
                        </FormControl>
                        <FormControl style={{ width: '100%', marginTop: '10px' }}>
                          <InputLabel id="diffToStartDate">開始日</InputLabel>
                          <Select
                            labelId="diffToStartDate"
                            value={openMeeting.diffToStartDate}
                            onChange={e => handleChangeSelectedStartDate(e)}
                          >
                            {openMeetingStartDateOptions.map(o => (
                              <MenuItem value={o.value} key={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl style={{ width: '100%', marginTop: '20px' }}>
                          <InputLabel id="period">期間</InputLabel>
                          <Select
                            labelId="period"
                            value={openMeeting.period}
                            onChange={e => handleChangePeriod(e)}
                          >
                            {periodOptions.map(o => (
                              <MenuItem value={o.value} key={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {openMeeting.period === '' && (
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                              <DatePicker
                                autoOk
                                disableToolbar
                                disablePast
                                variant="inline"
                                format="YYYY/MM/DD(dd)"
                                margin="none"
                                value={openMeeting.endDate}
                                onChange={date => handleChangeEndDate(date)}
                                style={{ marginRight: '20px' }}
                              />
                            </MuiPickersUtilsProvider>
                          )}
                        </FormControl>
                      </LeftSectionInSection>
                      <FormControl style={{ width: '100%' }}>
                        <InputLabel id="eventsPerDay">1日の上限予定数</InputLabel>
                        <Select
                          labelId="eventsPerDay"
                          value={openMeeting.eventsPerDay}
                          onChange={e => handleChangeEventsPerDay(e)}
                        >
                          {eventsPerDayOptions.map(o => (
                            <MenuItem value={o} key={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl style={{ width: '100%' }}>
                        <LeftSectionSmallTitle>候補から除外する日時</LeftSectionSmallTitle>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          onClick={() => setShowBlockedSlotsForm(true)}
                          style={{ width: '100%', margin: '5px 20px 10px 0', padding: 10, background: '#fff' }}
                        >
                          除外日時を設定する
                        </Button>
                        {openMeeting.openMeetingBlockedSlots.length > 0 ? (
                          <>
                            {openMeeting.openMeetingBlockedSlots
                              .sort((a, b) => (a.startAt < b.startAt ? -1 : 1))
                              .map(s => (
                                <LeftBlockedSlotText key={s.startAt.format()}>{s.timeText}</LeftBlockedSlotText>
                              ))}
                          </>
                        ) : (
                          <LeftNoBlockedSlotText>除外日時なし</LeftNoBlockedSlotText>
                        )}
                      </FormControl>
                    </LeftSectionIn>
                  </LeftSection>
                  <LeftSection>
                    <LeftSectionTitle>会議詳細</LeftSectionTitle>
                    <LeftSectionIn padding="20px 20px 0 20px">
                      <FormControl style={{ width: '100%', marginTop: '10px' }}>
                        <InputLabel shrink htmlFor="open-meeting-meeting-type">
                          Web会議
                        </InputLabel>
                        <NativeSelect
                          value={openMeeting.meetingType}
                          onChange={e => handleChangeMeetingType(e.target.value as OpenMeetingType)}
                          inputProps={{
                            name: 'meeting_type',
                            id: 'open-meeting-meeting-type'
                          }}
                        >
                          <option value="none">なし</option>
                          {calendars.some(c => c.provider === 'google') && (
                            <option value="google">Google Meet</option>
                          )}
                          <option value="zoom">Zoom</option>
                        </NativeSelect>
                      </FormControl>
                      <Box>{renderZoomAuthDialog()}</Box>
                      <br />
                      <PlaceInput
                        value={openMeeting.place}
                        label="場所"
                        placeholder="場所を追加"
                        onInputChange={value => {
                          openMeeting.place = value;
                          if (value.length === 0) {
                            openMeeting.placeAddress = '';
                          }
                        }}
                        onChange={value => {
                          if (!value || typeof value === 'string') {
                            return;
                          }
                          openMeeting.placeAddress = value.subText;
                        }}
                        helperText={openMeeting.placeAddress}
                      />
                      <br />
                      <Box display="flex" style={{ alignItems: 'center' }}>
                        <LeftSectionSmallTitle style={{ marginRight: 'auto' }}>
                          この予定を公開する
                        </LeftSectionSmallTitle>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={openMeeting.published}
                              onChange={e => {
                                openMeeting.status = e.target.checked ? 'published' : 'hidden';
                              }}
                            />
                          }
                          label={<span style={{ fontSize: 10 }}>公開</span>}
                          labelPlacement="start"
                          style={{
                            color: openMeeting.published
                              ? muiTheme.palette.primary.main
                              : muiTheme.palette.grey[700],
                            marginTop: '10px'
                          }}
                        />
                      </Box>
                    </LeftSectionIn>
                  </LeftSection>
                </>
              )}
            </div>
            <LeftSection>
              {errorMessage && <Error color="error">{errorMessage}</Error>}
              {submitErrorMessage && <Error color="error">{submitErrorMessage}</Error>}
              {isMultiParticipantPrevented() && (
                <>
                  <ToDoMessage>複数名の参加者登録にはProプランが必要です</ToDoMessage>
                  {user?.aicalendarAdmin && (
                    <Link href="/organization/payments" target="_blank">
                      {trialUsed ? 'プランを見る' : '無料で試す'}
                    </Link>
                  )}
                </>
              )}
            </LeftSection>
            <LeftButtonSection>
              <Box display="flex" padding="0 20px">
                <Box flexGrow={999}>
                  <>
                    <LeftButtonWrap isMobile={contextValue.isMobile}>
                      {contextValue.isMobile && (
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          component={RouterLink}
                          to="/"
                          style={{
                            width: '100%',
                            marginRight: 20,
                            padding: 10,
                            background: '#fff',
                            lineHeight: 1
                          }}
                        >
                          キャンセル
                        </Button>
                      )}
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={!isSubmitButtonAvailable()}
                        onClick={() => handleValidation()}
                        style={{ width: contextValue.isMobile ? '100%' : '100px', padding: 10, lineHeight: 1 }}
                      >
                        決定
                      </Button>
                    </LeftButtonWrap>
                  </>
                </Box>
              </Box>
            </LeftButtonSection>
          </>
        )}
      </>
    );
  }
);

export const TheReAuthButton = styled(Button)`
  span.MuiButton-label {
    display: block;
    position: relative;
    width: auto;
    padding-right: 25px;
    &:before {
      content: '';
      position: absolute;
      top: 8px;
      right: 2px;
      width: 6px;
      height: 6px;
      border-top: 2px solid #fff;
      border-right: 2px solid #fff;
      -webkit-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
`;

export const TheLeftHead = styled(Box)`
  padding: 20px;
`;

const CloseButton = styled(CancelIcon)`
  color: #ddd;
`;

const TheTextField = styled(TextField)`
  font-size: 12px;
`;

const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-top: 6px;
  font-size: 10px;
`;

const ToDoMessage = styled(Typography).attrs({ color: 'textSecondary' })`
  font-size: 10px;
`;

export const AutocompleteBox = styled.div<{ withAlert?: string }>`
  width: 100%;
  .MuiAutocomplete-inputRoot.MuiFilledInput-root {
    padding-top: 6px;
  }

  ${props =>
    props.withAlert === 'true' &&
    `
    input::placeholder {
      color: ${muiTheme.palette.error.main};
      opacity: 1;
    }
    .MuiInput-underline:before {
      border-bottom-color: ${muiTheme.palette.error.main};
    }
  `}
`;

export const LeftSection = styled(Box)`
  margin-bottom: 20px;
`;

const LeftSectionTitle = styled.h3`
  margin: 0;
  padding: 10px 20px;
  background: #f9f9f9;
  border-left: solid 5px ${muiTheme.palette.primary.main};
  font-size: 12px;
`;

const LeftSectionIn = styled(Box)`
  padding: 20px;
`;

const LeftSectionInSection = styled(Box)`
  padding: 10px 0 20px 10px;
  margin-bottom: 10px;
`;

export const LeftButtonSection = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 10px 0;
  background: #f9f9f9;
`;

export const LeftButtonWrap = styled(Box)<{ isMobile: boolean }>`
  text-align: right;

  ${props =>
    props.isMobile &&
    `
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    width: 100vw;
    padding: 10px 16px;
    background: #f9f9f9;
  `}
`;

const LeftSectionSmallTitle = styled.h4`
  margin: 15px 0 0 0;
  font-size: 10px;
`;

const LeftBlockedSlotText = styled(Typography)`
  font-size: 10px;
  color: ${muiTheme.palette.primary.main};
`;

const LeftNoBlockedSlotText = styled(LeftBlockedSlotText)`
  color: #909090;
`;

const ZoomAuthDialog = styled(Dialog)``;

const ZoomAuthContent = styled(Box)`
  padding: 64px 56px 14px;
`;

const ZoomAuthButtonWrap = styled(Box)`
  display: flex;
  justify-content: center;
  background-color: #f9f9f9;
  padding: 14px 0;

  & button:first-child {
    margin-right: 10px;
  }

  & button {
    width: 105px;
  }
`;

export default Form;
